import { format } from 'date-fns';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { upsertChampionshipTeamApplication } from 'modules/teams/store/championship-teams/actions';
import { fetchChampionshipTeams } from 'modules/tournaments/store/championship/actions';
import { championshipSettingsSelector } from 'modules/tournaments/store/championship/selectors';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { SelectOption } from 'ud-form/components/select/component';
import UDText from 'ud-ui/components/text';
import { AnnouncedTeamStatus } from '../../../domain/enums/announced-team-status';
import { AnnouncedTeam } from '../../../domain/interfaces/announced-team';
import StyledSelect from '../../styled-select';
import ChampionshipTeamCardHeader from '../team-card/championship-team-card-header';
import { CardContainer } from '../team-card/styles';

type ChampionshipTeamCardProps = {
  index: number;
  championshipTeam: AnnouncedTeam;
  isUpdating?: boolean;
  championshipId: number;
};

const ChampionshipTeamCard = ({ index, championshipTeam, isUpdating, championshipId }: ChampionshipTeamCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const combinedUpdating = isUpdating || isSubmitting;

  const settings = useSelector(championshipSettingsSelector(championshipId));
  const maxPlayers = useMemo(() => {
    return settings?.applicationMaxPlayers ?? 28;
  }, [settings?.applicationMaxPlayers]);

  const handleCardClick = useCallback(() => {
    navigate(`${championshipTeam.id}`);
  }, [championshipTeam.id, navigate]);

  const handleStatusChange = useCallback(
    (option: SingleValue<SelectOption>) => {
      if (championshipTeam.applications.length === 0) {
        toast.error('Необходимо добавить участников в команду.');
        return;
      }

      // Pre-validate max allowed players in a team before accepting the whole team
      if (option?.value === AnnouncedTeamStatus.ACCEPTED) {
        const playersCount = championshipTeam.applications.length;
        if (typeof maxPlayers === 'number' && playersCount > maxPlayers) {
          toast.error(
            `Превышено допустимое количество игроков в команде. По условиям турнира максимальное количество игроков в команде: ${maxPlayers}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
            { autoClose: 1000 * 45 },
          );
          return;
        }
      }

      setIsSubmitting(true);
      dispatch(
        upsertChampionshipTeamApplication({
          teamId: championshipTeam.id,
          status: option?.value,
          championshipId,
        }),
      )
        .unwrap()
        .then((result) => {
          StepperMainTool.setByPath('stepper:main:applicationTeam', true);
          if (result?.status === AnnouncedTeamStatus.ACCEPTED) {
            toast.success('Все игроки команда успешно заявлены.');
          } else {
            toast.success('Все заявки игроков команда успешно отклонены.');
          }
          dispatch(fetchChampionshipTeams(championshipId));
        })
        .catch((error: Error) => {
          console.log({ error });
          if (error.message.includes('limit exceeded')) {
            const regex = /(\d+)/g;
            const limit = error.message.match(regex)?.[0] ?? '-';
            toast.error(
              `Превышен лимит заявок на участие в чемпионате. По условиям доступно заявок на команду: ${limit}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Вероятно кол-во игроков в команде превышает допустимое кол-во заявок установленное в условиях турнира. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
              { autoClose: 1000 * 45 },
            );
          } else toast.error('Не удалось изменить статус всех игроков команды');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [championshipTeam.applications.length, championshipTeam.id, dispatch, championshipId, maxPlayers],
  );

  const prefixComponent = (
    <UDText type="subhead" className="bold mr-6 ml-3">
      {index}
    </UDText>
  );

  const applicationDate = championshipTeam.applicationDate ? new Date(championshipTeam.applicationDate) : new Date();

  const bodyComponent = (
    <div className="flex items-center justify-between" style={{ flexBasis: '50%' }}>
      <UDText type="subhead" style={{ flexBasis: '30%' }}>
        {format(applicationDate, 'dd.MM.yyyy')}
      </UDText>
      <div className="flex grow items-center justify-between">
        <UDText type="subhead" className="team-players-count">
          {t('teams.playersCount', {
            count: championshipTeam.applications.length,
          })}
        </UDText>
        <StyledSelect
          isLoading={combinedUpdating}
          isDisabled={combinedUpdating}
          placeholder="Выбрать"
          className="interactive"
          options={[
            {
              value: AnnouncedTeamStatus.ACCEPTED,
              label: 'Подтвердить',
            },
            {
              value: AnnouncedTeamStatus.DECLINED,
              label: 'Отклонить',
            },
          ]}
          onChange={handleStatusChange}
        />
      </div>
    </div>
  );

  return (
    <CardContainer className="mb-4 last:mb-0" onClick={handleCardClick}>
      <ChampionshipTeamCardHeader
        isExpanded={false}
        containerProps={{ style: { cursor: 'pointer' } }}
        teamName={championshipTeam.name}
        shortName={championshipTeam.shortName}
        teamLogo={championshipTeam.logo?.url}
        prefixComponent={prefixComponent}
        actions={bodyComponent}
      />
    </CardContainer>
  );
};

export default ChampionshipTeamCard;
